// @tg-seq: 10849
// @tg-target: scr_tgc_10849_quick_help_atm

/**
 * Quick Help ATM - "Red Call Button" Pattern
 *
 * Displayed when user taps badge at Floor 3 directory but has no org association,
 * or clicks the bug/help icon in PlatformHeader with no auto-detected error.
 * Options:
 *   0. New User? Sign Up
 *   0b. New Organization Signup
 *   0c. Request Organization Association
 *   1. "I forgot my key" - Employee credential recovery
 *   2. "I am a guest" - Guest access request
 *   3. "I have QR code + PIN" - Event-based access
 *
 * MOVED (2026-09-30): was ComponentTemplates/DirectoryPatterns/AccessRequestModal.tsx
 * (seq: null, not resolvable by resolveComponentBySeq). Registered here at seq 10849
 * so it's a real routable/resolvable component while still being usable as a
 * controlled overlay anywhere via the open/onClose/organizationUuid props.
 *
 * Usage:
 *   <Self
 *     open={showModal}
 *     onClose={() => setShowModal(false)}
 *     organizationUuid="..."
 *   />
 */

import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import CloseIcon from '@mui/icons-material/Close';
import DomainAddIcon from '@mui/icons-material/DomainAdd';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import QrCodeOutlinedIcon from '@mui/icons-material/QrCodeOutlined';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../../utils/apiService';
import { getTsxFileName } from '../../utils/componentIdentity';
import { placedButtonOnClick } from '../../utils/directoryNavigation';

interface AccessRequestModalProps {
    open: boolean;
    onClose: () => void;
    organizationUuid?: string;
}

type RequestType = 'forgot_key' | 'guest' | 'qr_code' | null;

// Unauthenticated call (see CreateUserForm.tsx) - apiRequest() requires an existing
// access token and rejects immediately without one, which breaks a truly locked-out
// user trying to reset their password.
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

const Self: React.FC<AccessRequestModalProps> = ({ open, onClose, organizationUuid }) => {
    const navigate = useNavigate();
    const [requestType, setRequestType] = useState<RequestType>(null);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Form fields (guest access)
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [jobTitle, setJobTitle] = useState('');
    const [department, setDepartment] = useState('');
    const [associationType, setAssociationType] = useState(3); // Default: Employee
    const [employeeId, setEmployeeId] = useState('');

    // Forgot key / self-service password reset fields
    const [resetUsername, setResetUsername] = useState('');
    const [resetContactEmail, setResetContactEmail] = useState('');
    const [resetNewPassword, setResetNewPassword] = useState('');
    const [resetConfirmPassword, setResetConfirmPassword] = useState('');

    // QR code fields
    const [qrCode, setQrCode] = useState('');
    const [qrPin, setQrPin] = useState('');

    const handleReset = () => {
        setRequestType(null);
        setSubmitted(false);
        setError(null);
        setFirstName('');
        setLastName('');
        setEmail('');
        setPhone('');
        setJobTitle('');
        setDepartment('');
        setEmployeeId('');
        setAssociationType(3);
        setQrCode('');
        setQrPin('');
        setResetUsername('');
        setResetContactEmail('');
        setResetNewPassword('');
        setResetConfirmPassword('');
    };

    const handleClose = () => {
        handleReset();
        onClose();
    };

    const handleSubmitGuestAccess = async () => {
        if (!firstName || !lastName || !email) {
            setError('First name, last name, and email are required');
            return;
        }

        if (!organizationUuid) {
            setError('Organization context required');
            return;
        }

        setSubmitting(true);
        setError(null);

        try {
            await apiRequest('/api/access-control/request', 'POST', {
                access_request_type_id: 2,
                organization_uuid: organizationUuid,
                first_name: firstName,
                last_name: lastName,
                email: email,
                phone: phone || null,
                job_title: jobTitle || null,
                department: department || null,
                organization_employee_identifier: employeeId || null,
                org_user_association_type_id: associationType,
                priority: 'Medium',
            });

            setSubmitted(true);
        } catch (err: any) {
            setError(err.message || 'Failed to submit request');
        } finally {
            setSubmitting(false);
        }
    };

    // "I Forgot My Key" - real self-service password reset (not an admin-approval
    // request). Identity is confirmed by requiring BOTH the username AND the contact
    // email on file to match (see api-starter/routes_auth.py POST /api/auth/reset-password).
    // Uses a raw fetch() (not apiRequest/apiService) because the user is, by definition,
    // not authenticated at this point - same pattern as CreateUserForm.tsx.
    const handleSubmitForgotKeyReset = async () => {
        if (!resetUsername || !resetContactEmail || !resetNewPassword || !resetConfirmPassword) {
            setError('All fields are required');
            return;
        }
        if (resetNewPassword.length < 8) {
            setError('New password must be at least 8 characters');
            return;
        }
        if (resetNewPassword !== resetConfirmPassword) {
            setError('New password and confirmation do not match');
            return;
        }

        setSubmitting(true);
        setError(null);

        try {
            const response = await fetch(`${API_BASE_URL}/api/auth/reset-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    username: resetUsername,
                    contact_email: resetContactEmail,
                    new_password: resetNewPassword,
                }),
            });

            const data = await response.json();

            if (!response.ok || data.success === false) {
                throw new Error(data.detail || data.error || 'Failed to reset password');
            }

            setSubmitted(true);
        } catch (err: any) {
            setError(err.message || 'Failed to reset password');
        } finally {
            setSubmitting(false);
        }
    };

    const handleSubmitQRCode = async () => {
        if (!qrCode || !qrPin) {
            setError('Both QR code and PIN are required');
            return;
        }

        setSubmitting(true);
        setError(null);

        try {
            await apiRequest('/api/access-control/request', 'POST', {
                access_request_type_id: 4, // QR_EVENT
                organization_uuid: organizationUuid,
                qr_code: qrCode,
                qr_pin: qrPin,
                priority: 'Medium',
            });

            setSubmitted(true);
        } catch (err: any) {
            setError(err.message || 'Failed to submit request');
        } finally {
            setSubmitting(false);
        }
    };

    // ===== Success Screen =====
    if (submitted) {
        const isPasswordReset = requestType === 'forgot_key';
        return (
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Typography variant="h6">
                            {isPasswordReset ? 'Password Reset' : 'Request Submitted'}
                        </Typography>
                        <IconButton onClick={handleClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Box
                            sx={{
                                width: 80,
                                height: 80,
                                margin: '0 auto',
                                borderRadius: '50%',
                                bgcolor: 'success.main',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mb: 3,
                            }}
                        >
                            <CheckCircleOutlineIcon sx={{ fontSize: 48, color: 'white' }} />
                        </Box>
                        <Typography variant="h6" gutterBottom>
                            {isPasswordReset
                                ? '✅ Password Reset Successfully'
                                : '✅ Request Submitted Successfully'}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: 'text.secondary',
                                mb: 3,
                            }}
                        >
                            {isPasswordReset
                                ? 'Your password has been updated. You can now log in with your new password.'
                                : "Your access request has been sent to the organization administrators. You'll receive an email notification when your request is processed."}
                        </Typography>
                        <Button variant="contained" onClick={handleClose} sx={{ px: 4 }}>
                            Close
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>
        );
    }

    // ===== Main Request Type Selection =====
    if (!requestType) {
        return (
            <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Typography variant="h6">🏧 Help ATM Menu</Typography>
                        <IconButton onClick={handleClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'text.secondary',
                            mb: 3,
                        }}
                    >
                        You don't have access to this organization. Select an option below to
                        request access:
                    </Typography>

                    <Box sx={{ display: 'grid', gap: 2 }}>
                        {/* Option 0: New User Sign Up — clean separation, routes to the full kiosk page (not an embedded form) */}
                        <Card
                            onClick={() => {
                                handleClose();
                                placedButtonOnClick({ tsx: 10023 }, navigate);
                            }}
                            sx={{
                                cursor: 'pointer',
                                transition: 'all 0.3s',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: 4,
                                    borderColor: 'primary.main',
                                },
                                border: '2px solid',
                                borderColor: 'divider',
                            }}
                        >
                            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <HowToRegIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="h6" gutterBottom>
                                        New User? Sign Up
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: 'text.secondary',
                                        }}
                                    >
                                        I don't have an account yet — create one and request
                                        organization access
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>

                        {/* Option 0b: New Organization Signup — routes directly to the org sign-up form
                            (seq 10853, scr_tgc_10853_new_organization_setup). Login-gated: that page shows
                            an inline login itself if the user isn't authenticated yet.
                            NOTE (2026-09-30): was hardcoded to the now-dead '/scr_tgc_10830_new_organization_setup'
                            path — that component was renamed/re-sequenced to 10853. Fixed to use
                            placedButtonOnClick(seq) so future renames can't silently break this button again. */}
                        <Card
                            onClick={() => {
                                handleClose();
                                placedButtonOnClick({ tsx: 10853 }, navigate);
                            }}
                            sx={{
                                cursor: 'pointer',
                                transition: 'all 0.3s',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: 4,
                                    borderColor: 'primary.main',
                                },
                                border: '2px solid',
                                borderColor: 'divider',
                            }}
                        >
                            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <DomainAddIcon sx={{ fontSize: 48, color: 'secondary.main' }} />
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="h6" gutterBottom>
                                        New Organization Signup
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: 'text.secondary',
                                        }}
                                    >
                                        I need to register a brand-new organization on the platform
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>

                        {/* Option 0c: Request Organization Association — for an existing user who
                            needs to be linked to an organization (search + request, org admin
                            approves later). Routes to seq 10850 (org_pfuser_association_request_mgr). */}
                        <Card
                            onClick={() => {
                                handleClose();
                                placedButtonOnClick({ tsx: 10850 }, navigate);
                            }}
                            sx={{
                                cursor: 'pointer',
                                transition: 'all 0.3s',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: 4,
                                    borderColor: 'primary.main',
                                },
                                border: '2px solid',
                                borderColor: 'divider',
                            }}
                        >
                            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <BusinessIcon sx={{ fontSize: 48, color: 'success.main' }} />
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="h6" gutterBottom>
                                        Request Organization Association
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: 'text.secondary',
                                        }}
                                    >
                                        I already have an account — request to be associated with an
                                        organization
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>

                        {/* Option 1: Forgot Key */}
                        <Card
                            onClick={() => setRequestType('forgot_key')}
                            sx={{
                                cursor: 'pointer',
                                transition: 'all 0.3s',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: 4,
                                    borderColor: 'primary.main',
                                },
                                border: '2px solid',
                                borderColor: 'divider',
                            }}
                        >
                            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <BadgeOutlinedIcon sx={{ fontSize: 48, color: 'warning.main' }} />
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="h6" gutterBottom>
                                        I Forgot My Key
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: 'text.secondary',
                                        }}
                                    >
                                        I'm an employee/contractor but forgot my credentials
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>

                        {/* Option 2: Guest Access */}
                        <Card
                            onClick={() => setRequestType('guest')}
                            sx={{
                                cursor: 'pointer',
                                transition: 'all 0.3s',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: 4,
                                    borderColor: 'primary.main',
                                },
                                border: '2px solid',
                                borderColor: 'divider',
                            }}
                        >
                            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <PersonAddOutlinedIcon sx={{ fontSize: 48, color: 'info.main' }} />
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="h6" gutterBottom>
                                        I Am a Guest
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: 'text.secondary',
                                        }}
                                    >
                                        I'm a guest, vendor, or consultant requesting access
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>

                        {/* Option 3: QR Code */}
                        <Card
                            onClick={() => setRequestType('qr_code')}
                            sx={{
                                cursor: 'pointer',
                                transition: 'all 0.3s',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: 4,
                                    borderColor: 'primary.main',
                                },
                                border: '2px solid',
                                borderColor: 'divider',
                            }}
                        >
                            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <QrCodeOutlinedIcon sx={{ fontSize: 48, color: 'success.main' }} />
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="h6" gutterBottom>
                                        I Have QR Code + PIN
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: 'text.secondary',
                                        }}
                                    >
                                        Access from marketing event, tradeshow, or conference
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Box>
                </DialogContent>
            </Dialog>
        );
    }

    // ===== QR Code Form =====
    if (requestType === 'qr_code') {
        return (
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Typography variant="h6">QR Code Access</Typography>
                        <IconButton onClick={handleClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 1 }}>
                        <Typography
                            variant="body2"
                            sx={{
                                color: 'text.secondary',
                                mb: 3,
                            }}
                        >
                            Enter the QR code and PIN you received at the event
                        </Typography>

                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}

                        <TextField
                            fullWidth
                            label="QR Code"
                            value={qrCode}
                            onChange={(e) => setQrCode(e.target.value)}
                            sx={{ mb: 2 }}
                            required
                        />

                        <TextField
                            fullWidth
                            label="PIN"
                            value={qrPin}
                            onChange={(e) => setQrPin(e.target.value)}
                            type="password"
                            sx={{ mb: 3 }}
                            required
                        />

                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                            <Button onClick={() => setRequestType(null)}>Back</Button>
                            <Button
                                variant="contained"
                                onClick={handleSubmitQRCode}
                                disabled={submitting || !qrCode || !qrPin}
                            >
                                {submitting ? <CircularProgress size={24} /> : 'Submit Request'}
                            </Button>
                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>
        );
    }

    // ===== Forgot Key / Self-Service Password Reset Form =====
    if (requestType === 'forgot_key') {
        return (
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Typography variant="h6">Reset Your Password</Typography>
                        <IconButton onClick={handleClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 1 }}>
                        <Typography
                            variant="body2"
                            sx={{
                                color: 'text.secondary',
                                mb: 3,
                            }}
                        >
                            Enter your username and the contact email on file, then choose a new
                            password.
                        </Typography>

                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}

                        <TextField
                            fullWidth
                            label="Username"
                            value={resetUsername}
                            onChange={(e) => setResetUsername(e.target.value)}
                            sx={{ mb: 2 }}
                            required
                        />

                        <TextField
                            fullWidth
                            label="Contact Email"
                            type="email"
                            value={resetContactEmail}
                            onChange={(e) => setResetContactEmail(e.target.value)}
                            sx={{ mb: 2 }}
                            required
                        />

                        <TextField
                            fullWidth
                            label="New Password"
                            type="password"
                            value={resetNewPassword}
                            onChange={(e) => setResetNewPassword(e.target.value)}
                            sx={{ mb: 2 }}
                            required
                        />

                        <TextField
                            fullWidth
                            label="Confirm New Password"
                            type="password"
                            value={resetConfirmPassword}
                            onChange={(e) => setResetConfirmPassword(e.target.value)}
                            sx={{ mb: 3 }}
                            required
                        />

                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                            <Button onClick={() => setRequestType(null)}>Back</Button>
                            <Button
                                variant="contained"
                                onClick={handleSubmitForgotKeyReset}
                                disabled={
                                    submitting ||
                                    !resetUsername ||
                                    !resetContactEmail ||
                                    !resetNewPassword ||
                                    !resetConfirmPassword
                                }
                            >
                                {submitting ? <CircularProgress size={24} /> : 'Reset Password'}
                            </Button>
                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>
        );
    }

    // ===== Guest Access Form =====
    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                    <Typography variant="h6">Guest Access Request</Typography>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ pt: 1 }}>
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'text.secondary',
                            mb: 3,
                        }}
                    >
                        Please provide your information. An administrator will review your request.
                    </Typography>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, 1fr)',
                            gap: 2,
                            mb: 2,
                        }}
                    >
                        <TextField
                            label="First Name"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            required
                        />
                        <TextField
                            label="Last Name"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            required
                        />
                    </Box>

                    <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        sx={{ mb: 2 }}
                        required
                    />

                    <TextField
                        fullWidth
                        label="Phone (optional)"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        sx={{ mb: 2 }}
                    />

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, 1fr)',
                            gap: 2,
                            mb: 2,
                        }}
                    >
                        <TextField
                            label="Job Title (optional)"
                            value={jobTitle}
                            onChange={(e) => setJobTitle(e.target.value)}
                        />
                        <TextField
                            label="Department (optional)"
                            value={department}
                            onChange={(e) => setDepartment(e.target.value)}
                        />
                    </Box>

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, 1fr)',
                            gap: 2,
                            mb: 3,
                        }}
                    >
                        <FormControl fullWidth>
                            <InputLabel>Association Type</InputLabel>
                            <Select
                                value={associationType}
                                onChange={(e) => setAssociationType(Number(e.target.value))}
                                label="Association Type"
                            >
                                <MenuItem value={3}>Employee</MenuItem>
                                <MenuItem value={4}>Contractor</MenuItem>
                                <MenuItem value={5}>Vendor</MenuItem>
                                <MenuItem value={6}>Guest</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            label="Employee ID (optional)"
                            value={employeeId}
                            onChange={(e) => setEmployeeId(e.target.value)}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <Button onClick={() => setRequestType(null)}>Back</Button>
                        <Button
                            variant="contained"
                            onClick={handleSubmitGuestAccess}
                            disabled={submitting || !firstName || !lastName || !email}
                        >
                            {submitting ? <CircularProgress size={24} /> : 'Submit Request'}
                        </Button>
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

Self.displayName = getTsxFileName(import.meta.url); // @tg-target: scr_tgc_10849_quick_help_atm
export default Self;
